import { Injectable, Injector } from "@angular/core";
import { Params } from "@angular/router";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Vision, VisionBreezeModel, VisionStatus, VisionType } from "@common/ADAPT.Common.Model/organisation/vision";
import { VisionCategory, VisionCategoryBreezeModel } from "@common/ADAPT.Common.Model/organisation/vision-category";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { RouteService } from "@common/route/route.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { setReturnPathSearchParam } from "@common/ux/base-routed.component";
import { forkJoin, from, map, of, switchMap } from "rxjs";
import { BaseOrganisationService } from "../organisation/base-organisation.service";
import { CommonTeamsService } from "../teams/common-teams.service";
import { AssignIntegratedVisionDialogComponent } from "./assign-integrated-vision-dialog/assign-integrated-vision-dialog.component";
import { CaptureVisionDialogComponent } from "./capture-vision-dialog/capture-vision-dialog.component";
import { ReturnCategoryKey } from "./category-vision-accordion/category-vision-accordion.component";
import { CategoryIdParamKey, EditIntegratedVisionPageRoute, EditPersonalVisionPageRoute } from "./edit-vision-page/edit-vision-page.component";

@Injectable({
    providedIn: "root",
})
export class VisionService extends BaseOrganisationService {
    constructor(
        injector: Injector,
        private teamsService: CommonTeamsService,
    ) {
        super(injector);
    }

    public get defaultVisionSortFunction() {
        return SortUtilities.getSortByFieldFunction<Vision>(
            (v) => v.extensions.isPersonal ? 0 : 1, // integrated visions go to end
            (v) => v.person?.fullName,
        );
    }

    public editVisionForPerson(personId: number, teamId: number, categoryId?: number, addReturnPathText?: string, scrollToCategory = false) {
        let searchParams: Params = { [CategoryIdParamKey]: categoryId?.toString() };
        if (addReturnPathText) {
            if (scrollToCategory) {
                const routeService = this.injector.get(RouteService);
                routeService.updateSearchParameterValue(ReturnCategoryKey, categoryId?.toString(), true, true);
            }

            searchParams = setReturnPathSearchParam(this.injector, searchParams, EditPersonalVisionPageRoute.id, addReturnPathText,
                undefined, undefined, undefined, true);
        }

        return EditPersonalVisionPageRoute.gotoRoute({ personId, teamId }, searchParams);
    }

    public editIntegratedVision(teamId: number, categoryId?: number, addReturnPathText?: string, scrollToCategory = false) {
        let searchParams: Params = { [CategoryIdParamKey]: categoryId?.toString() };
        if (addReturnPathText) {
            if (scrollToCategory) {
                const routeService = this.injector.get(RouteService);
                routeService.updateSearchParameterValue(ReturnCategoryKey, categoryId?.toString(), true, true);
            }

            searchParams = setReturnPathSearchParam(this.injector, searchParams, EditIntegratedVisionPageRoute.id, addReturnPathText,
                undefined, undefined, undefined, true);
        }

        return EditIntegratedVisionPageRoute.gotoRoute({ teamId }, searchParams);
    }

    public getVisionsByTeam(teamId: number) {
        return this.getVisionsByPersonTeam(undefined, teamId);
    }

    public hasVisionsForTeam(teamId: number) {
        return this.getVisionsByTeam(teamId).pipe(
            map((visions) => visions.length > 0)
        );
    }

    public assignIntegratedVision(teamId: number) {
        const dialogService = this.injector.get(AdaptCommonDialogService);
        return dialogService.open(AssignIntegratedVisionDialogComponent, teamId);
    }

    public getVisionsForPerson(personId: number) {
        return this.commonDataService.getByPredicate(VisionBreezeModel,
            new MethodologyPredicate<Vision>("personId", "==", personId).and(
                new MethodologyPredicate<Vision>("type", "==", VisionType.Personal)));
    }

    public getIntegratedVisionsByTeam(teamId: number) {
        return this.commonDataService.getByPredicate(VisionBreezeModel,
            new MethodologyPredicate<Vision>("teamId", "==", teamId).and(
                new MethodologyPredicate<Vision>("type", "==", VisionType.Integrated)));
    }

    public getSummaryIntegratedVisionByTeam(teamId: number) {
        return this.getIntegratedVisionsByTeam(teamId).pipe(
            map((visions) => visions.find((v) => v.extensions.isSummary)),
        );
    }

    public getVisionsByPersonTeam(personId: number | undefined, teamId: number) {
        const predicate = personId
            ? new MethodologyPredicate<Vision>("personId", "==", personId).and(
                new MethodologyPredicate<Vision>("teamId", "==", teamId).and(
                    new MethodologyPredicate<Vision>("type", "==", VisionType.Personal)))
            : new MethodologyPredicate<Vision>("teamId", "==", teamId);
        return this.commonDataService.getByPredicate(VisionBreezeModel, predicate);
    }

    public areAllVisionsCompleted(visions: Vision[]) {
        return !!visions?.length && visions.every((v) => v.extensions.isCompleted);
    }

    public getVisionCategoriesForTeam(teamId: number) {
        return this.commonDataService.getByPredicate(VisionCategoryBreezeModel, new MethodologyPredicate<VisionCategory>("teamId", "==", teamId));
    }

    public createVisionCategoryForTeam(team: Team) {
        return this.commonDataService.create(VisionCategoryBreezeModel, {
            organisationId: team.organisationId,
            teamId: team.teamId,
            name: "",
            ordinal: 0,
        } as VisionCategory);
    }

    public openCaptureVisionDialog(teamId: number) {
        const dialogService = this.injector.get(AdaptCommonDialogService);
        return dialogService.open(CaptureVisionDialogComponent, teamId);
    }

    public captureVisionForTeam(teamId: number, participatingMemberIds: number[]) {
        return forkJoin([
            this.getVisionsByTeam(teamId),
            this.getVisionCategoriesForTeam(teamId),
            this.teamsService.getTeamById(teamId),
        ]).pipe(
            switchMap(([visions, categories, team]) => {
                // delete all visions with category which is not in categories
                const categoryIds = categories.map((c) => c.visionCategoryId);
                const visionsWithInvalidCategory = visions.filter((v) => v.visionCategoryId && !categoryIds.includes(v.visionCategoryId));
                const deleteInvalidCategoryOps = visionsWithInvalidCategory.length > 0
                    ? forkJoin(visionsWithInvalidCategory.map((v) => this.commonDataService.remove(v)))
                    : of(undefined);

                // delete visions with personId not in members
                const visionsToDelete = visions.filter((v) => v.extensions.isPersonal && v.personId && !participatingMemberIds.includes(v.personId));
                const deleteInvalidMemberOps = visionsToDelete.length > 0
                    ? forkJoin(visionsToDelete.map((v) => this.commonDataService.remove(v)))
                    : of(undefined);

                const membersCategories = participatingMemberIds.flatMap((memberId) => categories.map((c) => ({ personId: memberId, visionCategoryId: c.visionCategoryId })));
                // all existing visions will be set to InProgress
                visions
                    .filter((v) => membersCategories.some((mc) => v.extensions.isPersonal && mc.personId === v.personId && mc.visionCategoryId === v.visionCategoryId))
                    .forEach((v) => (v.status = VisionStatus.InProgress));

                // create vision for each member and each category if not already in visions
                const visionsToAdd = membersCategories
                    .filter((mc) => !visions.some((v) => v.extensions.isPersonal && v.personId === mc.personId && v.visionCategoryId === mc.visionCategoryId))
                    .map((mc) => this.commonDataService.create(VisionBreezeModel, {
                        organisationId: team!.organisationId,
                        teamId: team!.teamId,
                        type: VisionType.Personal,
                        status: VisionStatus.InProgress,
                        personId: mc.personId,
                        visionCategoryId: mc.visionCategoryId,
                    } as Vision));

                // all existing visions with type integrated and category in categoryIds will be set to InProgress
                visions
                    .filter((v) => v.type === VisionType.Integrated && v.visionCategoryId && categoryIds.includes(v.visionCategoryId))
                    .forEach((v) => (v.status = VisionStatus.InProgress));

                // create integrated vision for each category with personId null if not already in visions
                const integratedVisionsToAdd = categories
                    .filter((c) => !visions.some((v) => v.visionCategoryId === c.visionCategoryId && v.type === VisionType.Integrated))
                    .map((c) => this.commonDataService.create(VisionBreezeModel, {
                        organisationId: team!.organisationId,
                        teamId: team!.teamId,
                        type: VisionType.Integrated,
                        status: VisionStatus.InProgress,
                        visionCategoryId: c.visionCategoryId,
                    } as Vision));

                const integratedSummaryVision = visions.find((v) => !v.visionCategoryId && v.type === VisionType.Integrated);
                if (!integratedSummaryVision) {
                    integratedVisionsToAdd.push(this.commonDataService.create(VisionBreezeModel, {
                        organisationId: team!.organisationId,
                        teamId: team!.teamId,
                        type: VisionType.Integrated,
                        status: VisionStatus.InProgress,
                    } as Vision));
                } else {
                    integratedSummaryVision.status = VisionStatus.InProgress;
                }

                return forkJoin([deleteInvalidCategoryOps, deleteInvalidMemberOps, ...visionsToAdd, ...integratedVisionsToAdd]);
            }),
            switchMap(() => this.saveEntities()),
        );
    }

    public createVisionsForTeamForNewCategory(team: Team, newCategory: VisionCategory) {
        // don't have to check for existing visions as category is new
        return from(this.teamsService.promiseToGetActiveTeamMembers(team!)).pipe(
            switchMap((members) => {
                const newVisions = members.map((m) => this.commonDataService.create(VisionBreezeModel, {
                    organisationId: team.organisationId,
                    teamId: team.teamId,
                    type: VisionType.Personal,
                    status: VisionStatus.InProgress,
                    personId: m.personId,
                    visionCategoryId: newCategory.visionCategoryId,
                }));
                // create integrated vision for the category
                newVisions.push(this.commonDataService.create(VisionBreezeModel, {
                    organisationId: team.organisationId,
                    teamId: team.teamId,
                    type: VisionType.Integrated,
                    status: VisionStatus.InProgress,
                    visionCategoryId: newCategory.visionCategoryId,
                }));

                return forkJoin(newVisions);
            }),
        );
    }

    public extractIntegratedVisionText(visions: Vision[]) {
        let visionText = "";
        const summary = visions.find((v) => v.extensions.isIntegrated && v.extensions.isSummary);
        if (summary?.statement) {
            visionText += summary.statement;
        }

        const integratedVisions = visions
            .filter((v) => v.extensions.isIntegrated && !v.extensions.isSummary)
            .sort(SortUtilities.getSortByFieldFunction<Vision>(
                (v) => v.visionCategory?.ordinal || 0,
            ));
        integratedVisions.forEach((v) => {
            if (v.visionCategory?.name) {
                visionText += `<h3 class="mt-3">${v.visionCategory?.name}</h3>`;
            }

            if (v.statement) {
                visionText += v.statement;
            }
        });

        return visionText;
    }
}
